import React, { useEffect, useRef, useState } from 'react';
import apiService from '../../services/ApiService';
import publicAccessService from '../../services/PublicAccessService';

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
let turnstileScriptPromise = null;

const loadTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.turnstile));
      existingScript.addEventListener('error', () => reject(new Error('No pudimos cargar Cloudflare Turnstile.')));
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error('No pudimos cargar Cloudflare Turnstile.'));
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

const PublicCaptchaGate = ({ onVerified }) => {
  const siteKey = process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY;
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifiedRef = useRef(onVerified);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  useEffect(() => {
    if (publicAccessService.hasToken()) {
      onVerifiedRef.current?.();
      return undefined;
    }

    if (!siteKey) {
      setIsLoading(false);
      setError('Falta configurar la site key del captcha.');
      return undefined;
    }

    let cancelled = false;

    const renderTurnstile = async () => {
      try {
        setIsLoading(true);
        setError('');
        const turnstile = await loadTurnstileScript();
        if (cancelled || !containerRef.current || !turnstile) {
          return;
        }

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'light',
          callback: async (token) => {
            try {
              setIsVerifying(true);
              setError('');
              const response = await apiService.postPublic('/public-access/turnstile/verify', { token });
              publicAccessService.setToken(response.accessToken);
              onVerifiedRef.current?.(response.accessToken);
            } catch (verifyError) {
              setError(verifyError.message || 'No pudimos validar el captcha.');
              publicAccessService.clearToken();
              if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.reset(widgetIdRef.current);
              }
            } finally {
              setIsVerifying(false);
            }
          },
          'expired-callback': () => {
            publicAccessService.clearToken();
            setError('El captcha expiró. Volvé a validarlo.');
          },
          'error-callback': () => {
            publicAccessService.clearToken();
            setError('No pudimos inicializar el captcha. Intentá nuevamente.');
          }
        });
      } catch (scriptError) {
        if (!cancelled) {
          setError(scriptError.message || 'No pudimos cargar el captcha.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    renderTurnstile();

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  return (
    <div className="public-captcha-gate">
      <div className="public-captcha-gate__card">
        <span className="public-captcha-gate__eyebrow">Validación previa</span>
        <h2 className="public-captcha-gate__title">Antes de ingresar al catálogo</h2>
        <p className="public-captcha-gate__copy">
          Validá el captcha para habilitar el acceso a las materias y a los asistentes públicos.
        </p>
        <div className="public-captcha-gate__widget">
          <div ref={containerRef} />
        </div>
        {(isLoading || isVerifying) && (
          <p className="public-captcha-gate__status">
            {isVerifying ? 'Validando captcha...' : 'Cargando captcha...'}
          </p>
        )}
        {error && (
          <p className="public-captcha-gate__error">{error}</p>
        )}
      </div>
    </div>
  );
};

export default PublicCaptchaGate;
